<template>
    <v-card flat class="pa-2">

        <!--Run Created Confirmation-->
        <template v-if="run != null">
            <div class="pb-1">
                <span class="caption greyType--text">Data Uploaded</span>
            </div>
            <v-layout row wrap align-center class="pb-1">
                <v-flex shrink class="pr-2">
                    <v-chip small color="primary" text-color="white">
                        <v-icon small left>check_circle</v-icon>
                        {{ run.row_count }} rows detected
                    </v-chip>
                </v-flex>
                <v-flex shrink class="pr-2">
                    <v-chip small color="blue-grey" text-color="white">{{ run.description }}</v-chip>
                </v-flex>
            </v-layout>
            <div class="pb-1">
                <span class="caption greyType--text">Detected Columns</span>
            </div>
            <v-layout row wrap>
                <v-flex shrink v-for="(header, i) in (run.headers || [])" :key="'runHeader' + i" class="pr-1 pb-1">
                    <v-chip small>{{ header }}</v-chip>
                </v-flex>
            </v-layout>
            <div class="pt-1">
                <span class="caption greyType--text">This run has already been created. Continue to the Map step, or start a new import from the Report step once it is finished.</span>
            </div>
        </template>

        <!--New Run Form-->
        <template v-else>
            <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                    <div class="pb-1">
                        <span class="caption greyType--text">Import Template</span>
                    </div>
                    <v-autocomplete
                            dense
                            v-model="fkTemplate"
                            :items="templateItems"
                            label="Select A Template (Optional)"
                            item-text="description"
                            item-value="id"
                            hide-details
                            single-line
                            color="primary"
                            class="ma-0 pa-0"
                            clearable
                    ></v-autocomplete>
                </v-flex>
                <v-flex xs12 sm6>
                    <div class="pb-1">
                        <span class="caption greyType--text">Run Description</span>
                    </div>
                    <v-text-field
                            dense
                            v-model="description"
                            label="Run Description"
                            hide-details
                            single-line
                            color="primary"
                            class="ma-0 pa-0"
                            @input="descriptionDirty = true"
                    ></v-text-field>
                </v-flex>
            </v-layout>

            <div class="pt-2 pb-1">
                <span class="caption greyType--text">Data Source</span>
            </div>
            <v-btn-toggle v-model="sourceMode" mandatory class="elevation-1">
                <v-btn small flat value="file" color="primary">
                    <v-icon small left>attach_file</v-icon>
                    Upload File
                </v-btn>
                <v-btn small flat value="paste" color="primary">
                    <v-icon small left>content_paste</v-icon>
                    Paste Data
                </v-btn>
            </v-btn-toggle>

            <template v-if="sourceMode == 'file'">
                <input
                        type="file"
                        ref="fileInput"
                        accept=".csv,.txt,.xlsx"
                        style="display: none"
                        @change="onFileSelected"
                />
                <v-layout row align-center class="pt-2">
                    <v-flex shrink>
                        <v-btn
                                small
                                color="secondary"
                                class="ma-0"
                                :loading="readingFile"
                                :disabled="readingFile"
                                @click="$refs.fileInput.click()"
                        >
                            <v-icon small left>attach_file</v-icon>
                            {{ filename != null ? 'Choose A Different File' : 'Choose File' }}
                        </v-btn>
                    </v-flex>
                    <v-flex shrink class="pl-2" v-if="filename != null">
                        <v-chip small color="primary" text-color="white">
                            <v-icon small left>insert_drive_file</v-icon>
                            {{ filename }}
                        </v-chip>
                    </v-flex>
                </v-layout>
                <div class="pt-1">
                    <span class="caption greyType--text">Accepted file types: .csv, .txt, .xlsx</span>
                </div>
            </template>

            <template v-else>
                <div class="pt-2">
                    <v-textarea
                            dense
                            hide-details
                            class="pa-0 ma-0"
                            solo
                            label="Paste Raw Data (Include Column Headings In The First Row)"
                            v-model="raw"
                            rows="8"
                    ></v-textarea>
                </div>
                <div class="pt-1">
                    <span class="caption greyType--text">*Please include column headings in the pasted data</span>
                </div>
            </template>

            <div class="pt-2">
                <v-btn
                        color="secondary"
                        class="ma-0"
                        :disabled="!canContinue || busy"
                        :loading="busy"
                        @click="continueUpload"
                >
                    Continue
                    <v-icon right>chevron_right</v-icon>
                </v-btn>
            </div>
        </template>
    </v-card>
</template>

<script>

    import {codeBaseMixin} from "../../codeBaseMixin";

    export default {
        mixins: [codeBaseMixin],
        props: {
            templates: {
                type: Array,
                default: () => []
            },
            run: Object,
            busy: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                fkTemplate: null,
                description: 'Import ' + new Date().toLocaleDateString(),
                descriptionDirty: false,
                sourceMode: 'file',
                filename: null,
                contentB64: null,
                raw: null,
                readingFile: false
            }
        },
        computed: {
            templateItems() {
                return [{id: null, description: 'No Template'}].concat(this.templates || []);
            },
            canContinue() {
                if (this.description == null || this.description == '') {
                    return false;
                }
                if (this.sourceMode == 'file') {
                    return this.contentB64 != null && this.filename != null;
                }
                return this.raw != null && this.raw != '';
            }
        },
        methods: {
            onFileSelected(event) {
                let file = event.target.files != null && event.target.files.length > 0 ? event.target.files[0] : null;
                if (file == null) {
                    return;
                }
                this.readingFile = true;
                let reader = new FileReader();
                reader.onload = () => {
                    let result = reader.result;
                    this.contentB64 = result.substring(result.indexOf(',') + 1);
                    this.filename = file.name;
                    if (!this.descriptionDirty) {
                        this.description = file.name;
                    }
                    this.readingFile = false;
                    if (this.$refs.fileInput != null) {
                        this.$refs.fileInput.value = '';
                    }
                };
                reader.onerror = () => {
                    this.readingFile = false;
                    this.showSnack('Error', 'The file could not be read. Please try again.', 'Close', 'red');
                    if (this.$refs.fileInput != null) {
                        this.$refs.fileInput.value = '';
                    }
                };
                reader.readAsDataURL(file);
            },
            continueUpload() {
                if (!this.canContinue) {
                    return;
                }
                let payload = {
                    description: this.description
                };
                if (this.fkTemplate != null) {
                    payload.fk_template = this.fkTemplate;
                }
                if (this.sourceMode == 'file') {
                    payload.filename = this.filename;
                    payload.contentB64 = this.contentB64;
                } else {
                    payload.raw = this.raw;
                }
                this.$emit('create-run', payload);
            }
        }
    }
</script>

<style scoped>

</style>
